import type { GalleryImage } from "@productlane/lib/src/tiptap/extensions/image-gallery-extension"
import { toGalleryImages } from "@productlane/lib/src/tiptap/extensions/image-gallery-extension"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { EditorState, Transaction } from "@tiptap/pm/state"

// The alt lands seconds after the upload, so as a history step it would be the
// first thing Cmd+Z undoes, invisibly. Keep it out of the undo stack.
const withoutHistory = (tr: Transaction) => tr.setMeta("addToHistory", false)

const findNode = (
	state: EditorState,
	matches: (node: ProseMirrorNode) => boolean,
): number | null => {
	let found: number | null = null
	state.doc.descendants((node, pos) => {
		if (found !== null) return false
		if (matches(node)) {
			found = pos
			return false
		}
		return true
	})
	return found
}

export const applyImageAlt = (
	state: EditorState,
	fileUrl: string,
	altText: string,
): Transaction | null => {
	const pos = findNode(
		state,
		(node) =>
			node.type.name === "image" &&
			node.attrs.src === fileUrl &&
			!node.attrs.alt,
	)
	if (pos === null) return null
	return withoutHistory(state.tr.setNodeAttribute(pos, "alt", altText))
}

export const applyGalleryImageAlt = (
	state: EditorState,
	fileUrl: string,
	altText: string,
): Transaction | null => {
	let images: GalleryImage[] = []
	const pos = findNode(state, (node) => {
		if (node.type.name !== "imageGallery") return false
		const candidates = toGalleryImages(node.attrs.images)
		if (!candidates.some((image) => image.src === fileUrl && !image.alt)) {
			return false
		}
		images = candidates
		return true
	})
	if (pos === null) return null
	return withoutHistory(
		state.tr.setNodeAttribute(
			pos,
			"images",
			images.map((image) =>
				image.src === fileUrl && !image.alt
					? { ...image, alt: altText }
					: image,
			),
		),
	)
}
